import { useAdminExtras } from "@/hooks/useAdminExtras";
import { createFileRoute } from "@tanstack/react-router";
import { useEffect, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useServerFn } from "@tanstack/react-start";
import { toast } from "sonner";
import { ShieldCheck, Truck, AlertTriangle, KeyRound, Check, X } from "lucide-react";
import { AdminShell } from "@/components/AdminShell";
import { FeeCalculator } from "@/components/FeeCalculator";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Switch } from "@/components/ui/switch";
import { AUCTION_TRAFFIC_GOAL } from "@/lib/auction-availability";
import { Checkbox } from "@/components/ui/checkbox";
import {
  adminGetOrderSettings,
  adminUpdateOrderSettings,
  adminSetChecklistItem,
} from "@/lib/orders-admin.functions";
import {
  getExpertVisibility,
  setExpertVisibility,
  type ExpertVisibility,
} from "@/lib/expert-visibility.functions";

export const Route = createFileRoute("/_authenticated/admin/betalning-frakt")({
  component: PaymentShippingAdmin,
  head: () => ({
    meta: [
      { title: "Betalning och frakt – admin" },
      { name: "description", content: "Styr betalning, frakt, avgifter och vägen till skarpt läge." },
      { name: "robots", content: "noindex" },
    ],
  }),
});

const CHECKLIST: { key: string; label: string; hint: string }[] = [
  {
    key: "company",
    label: "Företag och bankkonto klart",
    hint: "Org.nr, firmatecknare och ett konto som pengarna kan landa på.",
  },
  {
    key: "stripe",
    label: "Stripe Connect-konto godkänt",
    hint: "Konto för marknadsplats (Express). Vi håller köparens pengar och betalar säljaren efter godkännande.",
  },
  {
    key: "kyc",
    label: "Rutin för säljarkontroll (KYC)",
    hint: "Stripe kräver ID-kontroll av säljare som tar emot pengar. Ingen utbetalning utan godkänd säljare.",
  },
  {
    key: "shipmondo",
    label: "Fraktavtal via Shipmondo eller nShift",
    hint: "Konto plus avtal med minst en transportör (PostNord, DHL, Budbee). Ger fraktsedlar och spårning.",
  },
  {
    key: "prices",
    label: "Fraktpriser avstämda mot avtalet",
    hint: "Våra priser i tabellen måste täcka inköpspriset, annars säljer vi frakt med förlust.",
  },
  {
    key: "terms",
    label: "Villkor för köp uppdaterade",
    hint: "Ångerrätt, reklamation, vem som är avtalspart och hur tvister hanteras.",
  },
  {
    key: "support",
    label: "Supportflöde för tvister bemannat",
    hint: "Någon måste kunna besluta om återbetalning eller utbetalning inom rimlig tid.",
  },
  {
    key: "webhook",
    label: "Webhookar inkopplade och testade",
    hint: "Betalstatus från Stripe och kollihändelser från fraktleverantören.",
  },
];

const RISKS: { title: string; body: string }[] = [
  {
    title: "Vi håller andras pengar",
    body: "Så fort köparens pengar stannar hos oss finns krav på hur de hanteras. Använd Stripe Connect så ligger pengarna hos Stripe, inte på vårt eget konto – annars kan det räknas som betaltjänst och kräva tillstånd.",
  },
  {
    title: "Bedrägeriförsök",
    body: "Vanligaste fallen: säljaren skickar en tom låda, köparen påstår att paketet var tomt, eller kort som är stulna. Motmedel: spårning som bevis, utbetalning först efter godkännande, och stopp för nya säljare med hög summa direkt.",
  },
  {
    title: "Återkrav från kortköp",
    body: "Köparen kan begära pengarna tillbaka via banken flera veckor efter köpet. Om vi redan betalat säljaren står vi för förlusten. Håll en buffert och spara alltid spårningshistoriken.",
  },
  {
    title: "Tvister tar tid",
    body: "Varje anmält ärende kräver en människa som bestämmer. Räkna med det innan volymen växer.",
  },
  {
    title: "Frakt som går fel",
    body: "Skadat eller borttappat paket ersätts av transportören bara upp till ett takbelopp och bara med rätt underlag. Dyra prylar bör hämtas på plats.",
  },
  {
    title: "Beroende av leverantörer",
    body: "Ligger Stripe eller fraktleverantören nere stannar affärerna. Övningsläget här gör att sajten fortsätter funka även då.",
  },
];

const PROVIDERS: { name: string; role: string; need: string }[] = [
  {
    name: "Stripe Connect",
    role: "Tar emot köparens betalning, håller den och betalar ut till säljaren.",
    need: "Företagskonto hos Stripe med Connect påslaget. Nyckel läggs in som STRIPE_SECRET_KEY.",
  },
  {
    name: "Shipmondo",
    role: "Fraktsedlar, ombudsval och spårning mot flera transportörer via ett API.",
    need: "Konto plus egna eller Shipmondos transportöravtal. Nyckel läggs in som SHIPMONDO_API_KEY.",
  },
  {
    name: "nShift (alternativ)",
    role: "Samma sak som Shipmondo men större, vanligare hos e-handel med volym.",
    need: "Avtal och integration. Byts in genom att peka om fraktdelen.",
  },
  {
    name: "Egna transportöravtal",
    role: "PostNord, DHL eller Budbee direkt utan mellanhand.",
    need: "Ett avtal per transportör och en integration per transportör. Mer jobb, lägre pris vid volym.",
  },
];

// Uppgifter om Blocket är sammanställda manuellt och behöver stämmas av mot
// Blockets hjälpcenter innan de används som beslutsunderlag.
const BLOCKET_FACT_DATE = "16 augusti 2026";
const BLOCKET_SOURCE = "https://hjalp.blocket.se";

const BLOCKET_FACTS: {
  topic: string;
  blocket: string;
  ours: string;
  verify?: boolean;
}[] = [
  {
    topic: "Vad tjänsten heter",
    blocket:
      "Blocket Paket med köpskydd. Köparen trycker på Köp i annonsen i stället för att kontakta säljaren, och affären görs klart inne på Blocket.",
    ours: "Trygg affär. Samma idé: köpknapp i annonsen i stället för en ren kontaktannons.",
  },
  {
    topic: "Var pengarna ligger",
    blocket:
      "Köparen betalar direkt vid köpet med kort eller Swish. Pengarna hålls hos Blockets betalpartner och betalas ut till säljaren först efter att köparen hämtat ut paketet och godkänt varan.",
    ours: "Samma modell via Stripe Connect. Pengarna hålls kvar tills köparen godkänner eller tiden går ut.",
  },
  {
    topic: "Vad Blocket tar betalt",
    blocket:
      "Köparen betalar frakten och en avgift för köpskyddet som räknas i procent på varans pris med ett lägsta och högsta belopp. Säljaren får varupriset utan avdrag. Blocket tjänar alltså på köparens avgift plus marginalen på frakten.",
    ours:
      "Vi tar i dag en serviceavgift på säljaren (se fältet högst upp) i stället för på köparen. Lättare att förklara för säljaren, men gör oss dyrare att jämföra mot Blocket för samma pryl.",

    verify: true,
  },
  {
    topic: "Frakten",
    blocket:
      "Fasta priser per storleksklass med ombudsleverans. Säljaren får en fraktsedel eller QR-kod i appen och lämnar in paketet hos ombud. Spårningen syns för båda parter.",
    ours: "Samma upplägg via Shipmondo: QR-kod till säljaren, publik spårningssida för köparen.",
  },
  {
    topic: "Tid att godkänna",
    blocket:
      "Köparen har ett par dagar på sig efter utlämning att anmäla ett problem. Händer inget godkänns köpet automatiskt och pengarna går till säljaren.",
    ours: "Samma princip, antal dagar styrs av fältet Dagar innan pengarna släpps.",
    verify: true,
  },
  {
    topic: "Paketet kommer aldrig fram",
    blocket:
      "Har säljaren aldrig lämnat in paketet avbryts affären och köparen får tillbaka hela beloppet. Tappas paketet bort under transporten drivs ett ärende mot transportören, och köparen får pengarna tillbaka via köpskyddet.",
    ours: "Vi kan i dag avbryta ordern och släppa tillbaka pengarna, men ersättningsärendet mot transportören måste skötas manuellt av oss.",
  },
  {
    topic: "Köparen hämtar inte ut paketet",
    blocket:
      "Paketet går i retur till säljaren. Köparen får varupriset tillbaka men blir vanligtvis av med fraktkostnaden, och säljaren får sin pryl tillbaka.",
    ours: "Inte automatiserat. Vi måste manuellt återbetala och stänga ordern när retur registreras.",
    verify: true,
  },
  {
    topic: "Köparen påstår att varan är trasig eller fel",
    blocket:
      "Köparen anmäler inom tidsfristen och laddar upp bilder. Blocket fryser utbetalningen, hämtar in säljarens version och kräver som regel att varan skickas tillbaka innan pengarna betalas ut. Blocket avgör vem som får rätt utifrån annonstexten, bilderna och chatthistoriken.",
    ours: "Vi har frysning av utbetalning och ett supportärende, men ingen färdig returrutin och ingen beslutsmall. Det är den största luckan.",
  },
  {
    topic: "Köparen ljuger eller bluffar",
    blocket:
      "Blocket bedömer bevisningen. Räcker inte köparens underlag betalas säljaren ut ändå. Vid misstänkt bedrägeri stängs kontot av och ärendet hänvisas till polisanmälan. Blocket ersätter inte utanför det som köpskyddet täcker.",
    ours: "Samma linje bör gälla hos oss: bevisning avgör, kontostängning vid upprepade fall, och tydligt i villkoren att vi inte är part i själva affären.",
  },
  {
    topic: "Varan är utanför köpskyddet",
    blocket:
      "Fordon, bostäder, tjänster och riktigt dyra eller skrymmande saker omfattas inte. Där sker affären mellan parterna precis som förr och Blocket tar inget ansvar.",
    ours: "Vi behöver samma undantagslista och en beloppsgräns innan skarpt läge.",
  },
  {
    topic: "Tvist som inte löser sig",
    blocket:
      "Går parterna inte att ena hänvisar Blocket vidare till betalpartnern, banken (återkrav på kortköpet), ARN eller polis. Blocket agerar inte domstol.",
    ours: "Vi skriver in samma hänvisning i köpvillkoren och i supportsvaren.",
  },
];


function PaymentShippingAdmin() {
  const fetchSettings = useServerFn(adminGetOrderSettings);
  const update = useServerFn(adminUpdateOrderSettings);
  const setItem = useServerFn(adminSetChecklistItem);
  const qc = useQueryClient();

  const { data } = useQuery({ queryKey: ["admin-order-settings"], queryFn: () => fetchSettings() });

  const [simulation, setSimulation] = useState(true);
  const [payments, setPayments] = useState(true);
  const [shipping, setShipping] = useState(true);
  const [auctions, setAuctions] = useState(false);
  const [fee, setFee] = useState(5);
  const [days, setDays] = useState(3);

  useEffect(() => {
    if (!data) return;
    setSimulation(data.simulationMode);
    setPayments(data.paymentsEnabled);
    setShipping(data.shippingEnabled);
    setAuctions(data.auctionsEnabled);
    setFee(data.feePercent);
    setDays(data.approvalDays);
  }, [data]);

  const save = useMutation({
    mutationFn: () =>
      update({
        data: {
          simulationMode: simulation,
          feePercent: fee,
          approvalDays: days,
          paymentsEnabled: payments,
          shippingEnabled: shipping,
          auctionsEnabled: auctions,
        },
      }),
    onSuccess: () => {
      toast.success("Sparat.");
      qc.invalidateQueries({ queryKey: ["admin-order-settings"] });
    },
    onError: (e: Error) => toast.error(e.message),
  });

  const check = useMutation({
    mutationFn: (v: { key: string; done: boolean }) => setItem({ data: v }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["admin-order-settings"] }),
    onError: (e: Error) => toast.error(e.message),
  });

  const checklist = data?.checklist ?? {};
  const doneCount = CHECKLIST.filter((c) => checklist[c.key]).length;

  const mode =
    payments && shipping
      ? "Betalning och frakt – hela Trygg affär."
      : payments && !shipping
        ? "Bara betalning. Köparen betalar via oss, säljaren löser leveransen själv."
        : !payments && shipping
          ? "Bara frakt. Fraktsedel och spårning via oss, betalning sker mellan parterna."
          : "Avstängt. Inga köp via oss – annonserna fungerar som vanligt.";

  const flowSteps: { title: string; body: string }[] = [
    {
      title: "Köparen trycker på Köp i annonsen",
      body: payments
        ? "Köparen betalar hela beloppet direkt. Pengarna hålls kvar hos betalpartnern, de går inte till säljaren än. Annonsen pausas automatiskt så att ingen annan köper samma pryl."
        : "Betalning är avstängd. Köparen reserverar prylen här och gör upp om betalningen direkt med säljaren. Annonsen pausas ändå.",
    },
    {
      title: "Säljaren accepterar och får en fraktsedel",
      body: shipping
        ? "Säljaren får en QR-kod som visas i mobilen hos ombudet. Ingen utskrift behövs och adressen behöver aldrig lämnas ut till köparen."
        : "Frakt via oss är avstängd. Säljaren och köparen kommer överens om upphämtning eller egen frakt.",
    },
    {
      title: "Paketet skickas och spåras",
      body: "Transportören skickar kollihändelser till oss via webhook. Både köpare och säljare ser samma spårning, och den finns publikt på spårningssidan med kollinumret.",
    },
    {
      title: `Köparen hämtar ut och har ${days} dagar på sig`,
      body: "Under de dagarna kan köparen godkänna direkt eller anmäla ett problem. Anmäls inget händer nästa steg av sig självt.",
    },
    {
      title: "Pengarna släpps till säljaren",
      body: payments
        ? `Vid godkännande eller när tiden gått ut betalas säljaren ut, minus vår serviceavgift på ${fee} procent.`
        : "Ingen utbetalning sker via oss eftersom betalning är avstängd. Ordern markeras bara som klar.",
    },
    {
      title: "Reklamation stoppar allt",
      body: "Anmäler köparen ett fel fryses utbetalningen, ordern låses och ärendet hamnar hos supporten. Först när någon fattat beslut går pengarna vidare eller tillbaka.",
    },
  ];

  const faq: { q: string; a: string[] }[] = [
    {
      q: "Varan kom fram trasig – vad gäller?",
      a: [
        "Köparen anmäler problemet på ordersidan inom godkännandefristen på " +
          days +
          " dagar och bifogar bilder på skadan, gärna även på emballaget.",
        "Utbetalningen fryses direkt och ordern låses. Ingen automatisk frisläppning sker medan tvisten är öppen.",
        "Supporten går igenom bevisen i Tvister och väljer utbetalning till säljaren, full återbetalning, delad lösning eller avslag. Beslutet motiveras för båda parter.",
        "Är skadan transportskada driver vi ersättningsärendet mot transportören själva – köparen ska aldrig behöva jaga fraktbolaget.",
      ],
    },
    {
      q: "Varan dyker aldrig upp – vem tar smällen?",
      a: [
        "Har säljaren aldrig skapat fraktsedel eller lämnat in paketet kan ordern avbrytas och hela beloppet, inklusive frakt, går tillbaka till köparen. Annonsen aktiveras igen.",
        "Står spårningen stilla eller är kollit borttappat återbetalar vi köparen och tar ersättningen med transportören. Säljaren ska inte stå utan pengar på grund av ett borttappat kolli när inlämning är styrkt.",
        "Vid hämtning på plats där ingen dök upp gäller den part som kan visa kontaktförsök i meddelanden – därför ska all kontakt ske här och inte via privata kanaler.",
        "Kom paketet i retur till säljaren återbetalas köparen och säljaren står för returfrakten om orsaken var fel adressuppgifter från säljaren.",
      ],
    },
    {
      q: "Köpare och säljare är inte överens – hur avgör vi?",
      a: [
        "Båda parter kan öppna en tvist och lägga in text och upp till fem bilder. Allt samlas i en tråd som supporten ser i Tvister.",
        "Vi väger annonsens beskrivning och bilder mot det köparen fått, spårningen och parternas meddelanden. Bevisbörda för att varan stämde med annonsen ligger på säljaren; bevisbörda för skada eller avvikelse ligger på köparen.",
        "Fyra beslut finns: betala ut till säljaren, full återbetalning, dela beloppet vid mindre avvikelser, eller avslå anmälan så att affären fortsätter som vanligt.",
        "Delad lösning används när prylen är användbar men inte som utlovad – köparen behåller varan och får en del av pengarna tillbaka.",
      ],
    },
    {
      q: "Hur lång tid har man på sig?",
      a: [
        "Köparen har " +
          days +
          " dagar från leverans att godkänna eller anmäla. Efter det frisläpps pengarna automatiskt.",
        "En öppen tvist stoppar automatiken helt – ordern ligger kvar tills ett beslut fattats.",
        "Den som anmält kan dra tillbaka sin anmälan, då återgår ordern till sitt tidigare läge och fristen fortsätter.",
      ],
    },
    {
      q: "Vad kostar det oss när något går fel?",
      a: [
        "Vid full återbetalning tar vi inte ut serviceavgiften på " +
          fee +
          " procent – hela beloppet går tillbaka till köparen.",
        "Vid delad lösning betalas säljaren ut på den kvarvarande delen, upp till ordinarie utbetalning.",
        "Övningsläge simulerar samtliga betalningar och återbetalningar, så flödet kan testas fullt ut innan skarpa nycklar läggs in.",
      ],
    },
    {
      q: "Vad gör vi vid misstänkt bedrägeri?",
      a: [
        "Ordern fryses, pengarna stannar hos oss och kontot kan pausas i Användare medan vi utreder.",
        "Vi begär kompletterande bevis – inlämningskvitto, bilder med datum, konversationen i meddelanden.",
        "Upprepade anmälningar mot samma konto ska alltid granskas samlat innan utbetalning sker.",
      ],
    },
  ];




  return (
    <AdminShell title="Betalning och frakt">
      <p className="text-sm text-muted-foreground">
        Här styr du vad Trygg affär innehåller, vad det kostar och när vi går skarpt.
      </p>

      {/* Läge */}
      <section className="mt-6 rounded-xl border border-border bg-card p-5">
        <h2 className="inline-flex items-center gap-2 font-heading text-lg font-semibold">
          <ShieldCheck className="h-5 w-5 text-primary" /> Vad är påslaget
        </h2>
        <p className="mt-1 text-sm text-muted-foreground">{mode}</p>

        <div className="mt-4 space-y-4">
          <ToggleRow
            label="Betalning via oss"
            hint="Köparen betalar här och pengarna hålls kvar tills prylen är godkänd."
            checked={payments}
            onChange={setPayments}
          />
          <ToggleRow
            label="Frakt via oss"
            hint="Vi skapar fraktsedel med QR-kod och visar spårning."
            checked={shipping}
            onChange={setShipping}
          />
          <ToggleRow
            label="Auktioner"
            hint={`Avstängt tills vi har ${AUCTION_TRAFFIC_GOAL} unika besökare per dygn. Påslaget visas budgivning publikt och nya auktioner kan skapas.`}
            checked={auctions}
            onChange={setAuctions}
          />
          <ToggleRow
            label="Övningsläge"
            hint="Inga riktiga pengar och inga riktiga fraktsedlar. Allt annat fungerar som skarpt."
            checked={simulation}
            onChange={setSimulation}
          />
        </div>

        <ExpertVisibilityRow />


        <div className="mt-5 grid gap-4 sm:grid-cols-2">
          <label className="text-sm">
            <span className="font-medium">Serviceavgift (%)</span>
            <Input
              type="number"
              min={0}
              max={30}
              step={0.5}
              value={fee}
              onChange={(e) => setFee(Number(e.target.value))}
              className="mt-1"
            />
          </label>
          <label className="text-sm">
            <span className="font-medium">Dagar innan pengarna släpps</span>
            <Input
              type="number"
              min={1}
              max={30}
              value={days}
              onChange={(e) => setDays(Number(e.target.value))}
              className="mt-1"
            />
          </label>
        </div>

        <Button className="mt-5" onClick={() => save.mutate()} disabled={save.isPending}>
          Spara inställningar
        </Button>
      </section>

      <FeeCalculator ourFeePercent={fee} />



      {/* Så går ett tryggt köp till hos oss */}
      <section className="mt-6 rounded-xl border border-border bg-card p-5">
        <h2 className="inline-flex items-center gap-2 font-heading text-lg font-semibold">
          <ShieldCheck className="h-5 w-5 text-primary" /> Så går ett tryggt köp till hos oss
        </h2>
        <p className="mt-1 text-sm text-muted-foreground">
          Pengarnas och paketets väg, steg för steg, med de inställningar som är sparade just nu.
        </p>

        <ol className="mt-4 space-y-4">
          {flowSteps.map((s, i) => (
            <li key={s.title} className="flex gap-3">
              <span className="mt-0.5 flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-primary/10 text-xs font-semibold text-primary">
                {i + 1}
              </span>
              <div>
                <p className="text-sm font-medium">{s.title}</p>
                <p className="text-sm text-muted-foreground">{s.body}</p>
              </div>
            </li>
          ))}
        </ol>

        <details className="mt-4 rounded-lg border border-border p-4">
          <summary className="cursor-pointer text-sm font-medium">
            Vad händer om något går fel hos oss
          </summary>
          <ul className="mt-3 list-disc space-y-2 pl-5 text-sm text-muted-foreground">
            <li>
              Säljaren skickar aldrig – ordern kan avbrytas i admin och hela beloppet går tillbaka
              till köparen. Annonsen läggs tillbaka som aktiv.
            </li>
            <li>
              Paketet tappas bort – spårningen står stilla. Vi återbetalar köparen och driver
              ersättningsärendet mot transportören själva.
            </li>
            <li>
              Köparen anmäler fel på varan – utbetalningen fryses direkt och ärendet hamnar i
              supporten. Ingen utbetalning förrän någon fattat beslut.
            </li>
            <li>
              Köparen hör inte av sig – efter {days} dagar godkänns köpet automatiskt och säljaren
              får sina pengar minus {fee} procent.
            </li>
          </ul>
        </details>
      </section>

      {/* Blocket */}
      <section className="mt-6 rounded-xl border border-border bg-card p-5">
        <h2 className="inline-flex items-center gap-2 font-heading text-lg font-semibold">
          <Truck className="h-5 w-5 text-primary" /> Så gör Blocket – och vad det betyder för oss
        </h2>
        <p className="mt-1 text-sm text-muted-foreground">
          Sammanställt {BLOCKET_FACT_DATE}. Rader märkta “kontrollera” innehåller belopp eller
          tidsgränser som Blocket ändrar då och då – stäm av mot{" "}
          <a
            href={BLOCKET_SOURCE}
            target="_blank"
            rel="noreferrer noopener"
            className="underline underline-offset-2"
          >
            Blockets hjälpcenter
          </a>{" "}
          innan siffrorna används i beslut.
        </p>

        <div className="mt-4 space-y-3">
          {BLOCKET_FACTS.map((f) => (
            <div key={f.topic} className="rounded-lg border border-border p-4">
              <p className="flex flex-wrap items-center gap-2 text-sm font-semibold">
                {f.topic}
                {f.verify ? (
                  <span className="rounded-full bg-amber-100 px-2 py-0.5 text-[11px] font-medium text-amber-800">
                    kontrollera
                  </span>
                ) : null}
              </p>
              <p className="mt-2 text-sm text-muted-foreground">
                <span className="font-medium text-foreground">Blocket: </span>
                {f.blocket}
              </p>
              <p className="mt-2 text-sm text-muted-foreground">
                <span className="font-medium text-foreground">Vårt läge: </span>
                {f.ours}
              </p>
            </div>
          ))}
        </div>
      </section>

      {/* FAQ */}
      <section className="mt-6 rounded-xl border border-border bg-card p-5">
        <h2 className="inline-flex items-center gap-2 font-heading text-lg font-semibold">
          <AlertTriangle className="h-5 w-5 text-primary" /> Vanliga frågor när det strular
        </h2>
        <p className="mt-1 text-sm text-muted-foreground">
          Använd svaren rakt av när ni svarar i supporten – de matchar hur Trygg affär faktiskt är
          byggd just nu ({days} dagars godkännandefrist, {fee} procent serviceavgift).
        </p>

        <div className="mt-4 space-y-3">
          {faq.map((item) => (
            <details key={item.q} className="rounded-lg border border-border p-4">
              <summary className="cursor-pointer text-sm font-medium">{item.q}</summary>
              <ul className="mt-3 list-disc space-y-2 pl-5 text-sm text-muted-foreground">
                {item.a.map((line) => (
                  <li key={line}>{line}</li>
                ))}
              </ul>
            </details>
          ))}
        </div>
      </section>




      {/* Nycklar */}
      <section className="mt-6 rounded-xl border border-border bg-card p-5">
        <h2 className="inline-flex items-center gap-2 font-heading text-lg font-semibold">
          <KeyRound className="h-5 w-5 text-primary" /> Nycklar
        </h2>
        <ul className="mt-3 space-y-2 text-sm">
          <KeyRow ok={Boolean(data?.paymentKeys)} name="Betalning (STRIPE_SECRET_KEY)" />
          <KeyRow ok={Boolean(data?.shippingKeys)} name="Frakt (SHIPMONDO_API_KEY)" />
        </ul>
        <p className="mt-3 text-sm text-muted-foreground">
          Övningsläget kan bara stängas av för de delar som har en nyckel inlagd.
        </p>
      </section>

      {/* Leverantörer */}
      <section className="mt-6 rounded-xl border border-border bg-card p-5">
        <h2 className="inline-flex items-center gap-2 font-heading text-lg font-semibold">
          <Truck className="h-5 w-5 text-primary" /> Leverantörer och vad de kräver
        </h2>
        <div className="mt-3 grid gap-3 sm:grid-cols-2">
          {PROVIDERS.map((p) => (
            <div key={p.name} className="rounded-lg border border-border p-4">
              <p className="font-semibold">{p.name}</p>
              <p className="mt-1 text-sm text-muted-foreground">{p.role}</p>
              <p className="mt-2 text-sm">{p.need}</p>
            </div>
          ))}
        </div>
      </section>

      {/* Checklista */}
      <section className="mt-6 rounded-xl border border-border bg-card p-5">
        <h2 className="font-heading text-lg font-semibold">
          Checklista inför skarpt läge ({doneCount}/{CHECKLIST.length})
        </h2>
        <ul className="mt-3 space-y-3">
          {CHECKLIST.map((c) => (
            <li key={c.key} className="flex gap-3">
              <Checkbox
                checked={Boolean(checklist[c.key])}
                onCheckedChange={(v) => check.mutate({ key: c.key, done: v === true })}
                aria-label={c.label}
                className="mt-0.5"
              />
              <div>
                <p className="text-sm font-medium">{c.label}</p>
                <p className="text-sm text-muted-foreground">{c.hint}</p>
              </div>
            </li>
          ))}
        </ul>
      </section>

      {/* Risker */}
      <section className="mt-6 rounded-xl border border-amber-300 bg-amber-50/60 p-5">
        <h2 className="inline-flex items-center gap-2 font-heading text-lg font-semibold">
          <AlertTriangle className="h-5 w-5 text-amber-600" /> Risker att ha koll på
        </h2>
        <div className="mt-3 space-y-3">
          {RISKS.map((r) => (
            <div key={r.title}>
              <p className="text-sm font-semibold">{r.title}</p>
              <p className="text-sm text-muted-foreground">{r.body}</p>
            </div>
          ))}
        </div>
      </section>
    </AdminShell>
  );
}

function ToggleRow(props: {
  label: string;
  hint: string;
  checked: boolean;
  onChange: (v: boolean) => void;
}) {
  return (
    <div className="flex items-start justify-between gap-4">
      <div>
        <p className="text-sm font-medium">{props.label}</p>
        <p className="text-sm text-muted-foreground">{props.hint}</p>
      </div>
      <Switch checked={props.checked} onCheckedChange={props.onChange} aria-label={props.label} />
    </div>
  );
}

const EXPERT_MODES: { id: ExpertVisibility; label: string; hint: string }[] = [
  { id: "off", label: "Av", hint: "Expertläget syns ingenstans, inte ens för dig." },
  { id: "admins", label: "Bara för mig", hint: "Syns enbart för administratörer och märks med Test." },
  { id: "public", label: "För alla", hint: "Syns publikt på sajten utan testmärkning." },
];

/** Superadmin styr om expertläget (Expert-planen och djupskanning) visas. */
function ExpertVisibilityRow() {
  const qc = useQueryClient();
  const read = useServerFn(getExpertVisibility);
  const write = useServerFn(setExpertVisibility);
  const { data } = useQuery({ queryKey: ["expert-visibility"], queryFn: () => read() });
  const mode = data?.mode ?? "admins";

  const save = useMutation({
    mutationFn: (m: ExpertVisibility) => write({ data: { mode: m } }),
    onSuccess: () => {
      toast.success("Sparat.");
      qc.invalidateQueries({ queryKey: ["expert-visibility"] });
    },
    onError: (e: Error) => toast.error(e.message),
  });

  return (
    <div className="mt-6 rounded-xl border border-border p-4">
      <p className="text-sm font-medium">Expertläget</p>
      <p className="mt-1 text-sm text-muted-foreground">
        Styr var Expert-kontot och djupskanningen visas – på prissidan, startsidan, i säljflödet och i texterna.
      </p>
      <div className="mt-3 grid gap-2 sm:grid-cols-3">
        {EXPERT_MODES.map((m) => (
          <button
            key={m.id}
            type="button"
            onClick={() => save.mutate(m.id)}
            aria-pressed={mode === m.id}
            className={`rounded-lg border p-3 text-left text-sm transition-colors ${
              mode === m.id ? "border-primary bg-primary/10" : "border-border hover:bg-muted"
            }`}
          >
            <span className="font-medium">{m.label}</span>
            <span className="mt-1 block text-xs text-muted-foreground">{m.hint}</span>
          </button>
        ))}
      </div>
      <MyExtrasRow />
    </div>
  );
}

/** Personligt val: se sajten som en vanlig inloggad användare eller med extrafunktioner. */
function MyExtrasRow() {
  const { extras, available, setExtras } = useAdminExtras();
  return (
    <div className="mt-4 rounded-lg border border-border p-3">
      <p className="text-sm font-medium">Mitt eget läge</p>
      <p className="mt-1 text-xs text-muted-foreground">
        {available
          ? "Av: du ser sajten precis som en vanlig inloggad användare. På: du ser test- och expertytorna."
          : "På den publika adressen visas aldrig test- eller expertytor, inte ens för dig."}
      </p>
      <button
        type="button"
        disabled={!available}
        onClick={() => setExtras(!extras)}
        aria-pressed={extras}
        className={`mt-3 rounded-lg border px-3 py-2 text-sm transition-colors disabled:opacity-50 ${
          extras ? "border-primary bg-primary/10" : "border-border hover:bg-muted"
        }`}
      >
        {extras ? "Extrafunktioner på" : "Visa som vanlig användare"}
      </button>
    </div>
  );
}

function KeyRow({ ok, name }: { ok: boolean; name: string }) {
  return (
    <li className="flex items-center gap-2">
      {ok ? (
        <Check className="h-4 w-4 text-emerald-600" />
      ) : (
        <X className="h-4 w-4 text-muted-foreground" />
      )}
      <span>{name}</span>
      <span className="text-muted-foreground">{ok ? "inlagd" : "saknas"}</span>
    </li>
  );
}
